import React, { useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import AgreementCheckboxes from './AgreementCheckboxes';
import {
  validateEmail,
  validatePhoneNumber,
  validateRequiredField,
} from '../../utils/competitionDay';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';
import PaymentChoice from '../payment/PaymentChoice';
import PaymentReconciliationNotice from '../payment/PaymentReconciliationNotice';
import { normaliseGuestEmail } from '@shared/guestEmail';

const client = generateClient({ authMode: 'apiKey' });

interface NonMemberRegistrationFormProps {
  eventId: string;
  event?: any; // Event object with nonMemberPrice
  onSuccess?: () => void;
  onBack?: () => void;
}

const NonMemberRegistrationForm: React.FC<NonMemberRegistrationFormProps> = ({
  eventId,
  event,
  onSuccess,
  onBack,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
  });
  const [agreements, setAgreements] = useState({
    clubRules: false,
    codeOfConduct: false,
    waiver: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [success, setSuccess] = useState(false);
  const [registrationDetails, setRegistrationDetails] = useState<any>(null);
  const [paymentDone, setPaymentDone] = useState(false);
  // #109: how the guest finished, so the confirmation can say so. The parent
  // onSuccess is only called from the Done button, never straight after
  // payment: SmartCheckInPage resets to the Member/Non-member picker on it,
  // which unmounted this screen before the guest saw any confirmation.
  const [paidHow, setPaidHow] = useState<'ONLINE' | 'ON_THE_DAY' | null>(null);
  // Backlog M10. Deliberately does NOT call the parent onSuccess() when set —
  // that callback navigates away from this screen (see CheckInPage.tsx), which
  // would hide this warning before the person could read it. Money was
  // charged; they need to see the reference before moving on.
  const [paymentReconciliationRef, setPaymentReconciliationRef] = useState<string | null>(null);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError('');
    setWarning('');
  };

  const checkExistingMember = async (email: string) => {
    try {
      const res: any = await (client.queries as any).memberEmailExists({ email });
      const data = parseLambdaResponse(res?.data);

      if (data?.exists && data?.isActive) {
        setWarning(
          `This email is associated with an active membership (${data.memberNumber}). ` +
            'Consider using Member Check-In or Family Check-In instead for a lower fee ($10 vs $15).'
        );
      }
    } catch (err) {
      console.error('Error checking for existing member:', err);
      // Don't block registration if check fails
    }
  };

  const handleEmailBlur = () => {
    if (formData.email && validateEmail(formData.email)) {
      checkExistingMember(formData.email);
    }
  };

  const validateForm = (): boolean => {
    try {
      validateRequiredField(formData.name, 'Name');
      validateRequiredField(formData.email, 'Email');
      validateRequiredField(formData.phone, 'Phone');

      if (!validateEmail(formData.email)) {
        setError('Please enter a valid email address.');
        return false;
      }

      if (!validatePhoneNumber(formData.phone)) {
        setError('Please enter a valid phone number.');
        return false;
      }

      if (!agreements.clubRules || !agreements.codeOfConduct || !agreements.waiver) {
        setError('Please accept all agreements to continue.');
        return false;
      }

      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    // Sunday hardening (G2): store the guest email trimmed and lower-cased so
    // the catch form matches it however the guest types it on the day.
    const guestEmail = normaliseGuestEmail(formData.email);

    try {
      // Check for duplicate registration via Lambda
      const dup: any = await (client.queries as any).checkRegistrationExists({
        eventId,
        email: guestEmail,
      });
      const dupData = parseLambdaResponse(dup?.data);
      if (dupData?.exists) {
        setError('This email is already registered for this competition.');
        setLoading(false);
        return;
      }

      // Create registration
      const res: any = await (client.mutations as any).submitCompetitionDayRegistration({
        eventId,
        participantName: formData.name,
        participantEmail: guestEmail,
        participantPhone: formData.phone,
        registrationType: 'NON_MEMBER',
      });
      const data = parseLambdaResponse(res?.data);
      if (!data?.success) {
        setError(
          data?.message || 'An error occurred while creating the registration. Please try again.'
        );
        setLoading(false);
        return;
      }

      setRegistrationDetails(data.registration);
      setSuccess(true);
    } catch (err) {
      console.error('Error creating registration:', err);
      setError('An error occurred while creating the registration. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-md">
        <div className="text-center">
          <div className="text-green-600 text-5xl mb-4" aria-hidden="true">✓</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {paymentDone ? 'Check-In Successful!' : 'Registration Successful!'}
          </h2>
          <p className="text-gray-600 mb-6">
            {paymentDone
              ? `${registrationDetails?.participantName || 'You'} ${registrationDetails?.participantName ? 'has' : 'have'} been checked in for the competition.`
              : 'You have been successfully registered for the competition.'}
          </p>

          <div className="bg-gray-50 p-4 rounded-lg mb-6 text-left">
            <h3 className="font-semibold text-gray-900 mb-2">Registration Details:</h3>
            <p className="text-sm">
              <span className="font-medium">Name:</span> {registrationDetails?.participantName}
            </p>
            <p className="text-sm">
              <span className="font-medium">Email:</span> {registrationDetails?.participantEmail}
            </p>
            <p className="text-sm">
              <span className="font-medium">Phone:</span> {registrationDetails?.participantPhone}
            </p>
            <p className="text-sm">
              <span className="font-medium">Fee:</span> ${registrationDetails?.feeAmount}
            </p>
            <p className="text-sm">
              <span className="font-medium">Status:</span>{' '}
              {paidHow === 'ONLINE' ? 'Paid' : paidHow === 'ON_THE_DAY' ? 'Pay on the day' : 'Pending Payment'}
            </p>
          </div>

          {paymentReconciliationRef && (
            <PaymentReconciliationNotice paymentReference={paymentReconciliationRef} />
          )}

          <p role={paymentDone ? 'status' : undefined} className="text-sm text-gray-600 mb-6">
            {paymentReconciliationRef
              ? ''
              : paidHow === 'ONLINE'
                ? "Payment complete. You're all set — good luck today!"
                : paidHow === 'ON_THE_DAY'
                  ? `You're all set. Please pay $${registrationDetails?.feeAmount ?? 15} at the sign-on desk. Good luck today!`
                  : "Choose how you'd like to pay your entry fee:"}
          </p>

          {!paymentDone && !paymentReconciliationRef && registrationDetails?.id && (
            <div className="mb-6 text-left">
              <PaymentChoice
                amountCents={1500}
                amountDisplay="$15.00"
                paymentType="COMPETITION_ENTRY"
                entityId={registrationDetails.id}
                entityType="COMPETITION_DAY_REGISTRATION"
                payerEmail={registrationDetails.participantEmail ?? ''}
                payerName={registrationDetails.participantName ?? ''}
                feeKey="competition_fee_nonmember"
                authMode="apiKey"
                onSuccess={() => {
                  setPaidHow('ONLINE');
                  setPaymentDone(true);
                }}
                onReconciliationRequired={(paymentId) => {
                  // Stay on screen — do NOT call the parent onSuccess here.
                  setPaymentReconciliationRef(paymentId);
                }}
                onPayOnDay={() => {
                  setPaidHow('ON_THE_DAY');
                  setPaymentDone(true);
                }}
              />
            </div>
          )}

          {paymentDone ? (
            <button
              onClick={() => {
                // #109: Done never returns to /check-in. The parent decides
                // where to go (SmartCheckInPage: the homepage); without one,
                // go home.
                if (onSuccess) return onSuccess();
                window.history.pushState({}, '', '/');
                window.dispatchEvent(new PopStateEvent('popstate'));
              }}
              className="min-h-[44px] bg-fishing-blue text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700"
            >
              Done
            </button>
          ) : (
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              Register Another Person
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Non-Member Registration</h2>

      <p className="text-gray-600 mb-6">
        Register for the competition as a non-member. Fee: $15 per person.
      </p>

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="John Smith"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            onBlur={handleEmailBlur}
            placeholder="john@example.com"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Phone Number <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            placeholder="0412 345 678"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        {warning && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm">
            <strong>Note:</strong> {warning}
          </div>
        )}

        <div className="mb-6">
          <AgreementCheckboxes agreements={agreements} onChange={setAgreements} />
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        <div className="bg-blue-50 p-4 rounded-lg mb-6">
          <p className="text-sm font-medium text-gray-900">
            Registration Fee: <span className="text-blue-600 text-lg">$15</span>
          </p>
          <p className="text-xs text-gray-600 mt-1">Includes BBQ breakfast and drinks</p>
        </div>

        <div className="flex gap-3">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Back
            </button>
          )}
          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {loading ? 'Registering...' : 'Complete Registration'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NonMemberRegistrationForm;
